// src/pages/ApplicationChampionshipMatches/components/MatchesFilterBox/index.tsx
import {
  Box,
  Button,
  Flex,
  Heading,
  InputGroup,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { UseFormReturn, useController, useWatch } from 'react-hook-form';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { SelectField } from 'src/components/SelectField';
import { InputField } from '@/components/InputField';
import ControlTeamSelectAutocomplete from '@/components/ControlTeamSelectAutocomplete';
import { formatDateToDateInputValue } from '@/utils/formatDateToDateInputValue';
import { GetParamsObject } from '@/model/query-params';
import { Club, MatchStatus, SeasonType } from '@/model';
import { useChampionships } from '@/services/queries/championship';
import { useSeasons } from '@/services/queries/season';
import { useStages, useStageById } from '@/services/queries/stage';
import { useSeasonTeams } from '@/services/queries/season-team';

import { MatchesFilters } from '../../types';

const I18N_PREFIX = 'pages.application_championship_matches.components.matches_filter_box';

export interface MatchesFilterBoxProps {
  filtersForm: UseFormReturn<MatchesFilters>;
  forceRefetch: () => void;
}

const DEFAULT_ROUND_FALLBACK = 50;

export const MatchesFilterBox = ({ filtersForm }: MatchesFilterBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, control, setValue } = filtersForm;

  const championshipUuid = useWatch({ control, name: 'championship_uuid' });
  const seasonUuid = useWatch({ control, name: 'season_uuid' });
  const stageUuid = useWatch({ control, name: 'stage_uuid' });

  // Lookups dos selects (cascade).
  const championshipsParams = useMemo(
    () => GetParamsObject({} as Record<string, string>, 1, 200, 'asc', 'name'),
    [],
  );
  const { data: championshipsData } = useChampionships(championshipsParams);

  const seasonsParams = useMemo(
    () =>
      championshipUuid
        ? GetParamsObject(
            { championship_uuid: championshipUuid } as Record<string, string>,
            1,
            200,
            'desc',
            'year',
          )
        : undefined,
    [championshipUuid],
  );
  const { data: seasonsData } = useSeasons(seasonsParams);

  const stagesParams = useMemo(
    () =>
      seasonUuid
        ? GetParamsObject(
            { season_uuid: seasonUuid } as Record<string, string>,
            1,
            200,
            'asc',
            'stage_order',
          )
        : undefined,
    [seasonUuid],
  );
  const { data: stagesData } = useStages(stagesParams);

  // Times participantes da temporada — só habilita o filtro de time após a
  // temporada ser escolhida; ajuda a reduzir a lista pra clubes relevantes.
  const seasonTeamsParams = useMemo(
    () =>
      seasonUuid
        ? GetParamsObject(
            { season_uuid: seasonUuid } as Record<string, string>,
            1,
            200,
            'asc',
            'created_at',
          )
        : undefined,
    [seasonUuid],
  );
  const { data: seasonTeamsData } = useSeasonTeams(seasonTeamsParams, !!seasonUuid);
  // ControlTeamSelectAutocomplete espera Club[] como seed. Deduplica por
  // team_uuid porque a tabela season_teams pode ter mais de uma linha para o
  // mesmo time (ex.: mesmo time presente em fases distintas no histórico).
  const seasonClubsSeed: Club[] = useMemo(() => {
    const map = new Map<string, Club>();
    (seasonTeamsData?.data ?? []).forEach(st => {
      const team = st.team;
      const uuid = st.team_uuid ?? team?.uuid;
      if (!team || !uuid || map.has(uuid)) return;
      map.set(uuid, {
        uuid,
        name: team.name,
        acronym: team.acronym,
        logo_path: team.logo_path ?? '',
        created_at: '',
        updated_at: '',
      });
    });
    return Array.from(map.values());
  }, [seasonTeamsData]);

  // Detalhe da stage selecionada — usado pra montar o select de Rodada
  // (1..current_round). Se current_round vier 0/null, fallback DEFAULT.
  const { data: selectedStage } = useStageById(stageUuid || '');

  const roundOptions = useMemo(() => {
    const total =
      selectedStage?.current_round && selectedStage.current_round > 0
        ? selectedStage.current_round
        : DEFAULT_ROUND_FALLBACK;
    return Array.from({ length: total }, (_, index) => index + 1);
  }, [selectedStage?.current_round]);

  // Inputs de data: formatamos com helper compartilhado para não bater com
  // diferenças de tz do componente date nativo do navegador.
  const startDateControl = useController({ name: 'start_date', control });
  const endDateControl = useController({ name: 'end_date', control });

  /** Trocar Championship limpa todos os filtros encadeados abaixo. */
  const handleChampionshipChange = (value: string) => {
    setValue('championship_uuid', value);
    setValue('season_uuid', '');
    setValue('stage_uuid', '');
    setValue('round', '');
    setValue('team_uuid', '');
  };

  const handleSeasonChange = (value: string) => {
    setValue('season_uuid', value);
    setValue('stage_uuid', '');
    setValue('round', '');
    setValue('team_uuid', '');
  };

  const handleStageChange = (value: string) => {
    setValue('stage_uuid', value);
    setValue('round', '');
  };

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>{t(`${I18N_PREFIX}.heading`)}</Heading>
      </Flex>

      <Flex direction={{ base: 'row' }} gap='small' rowGap='micro'>
        <SimpleGrid
          gap='deka'
          alignItems='flex-end'
          columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 7 }}
          rowGap='xxsmall'
          flexGrow={1}
        >
          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t(`${I18N_PREFIX}.championship_label`)}</Text>
            <SelectField
              {...register('championship_uuid')}
              onChange={event => handleChampionshipChange(event.target.value)}
            >
              <option value=''>{t(`${I18N_PREFIX}.all_option`)}</option>
              {(championshipsData?.data ?? []).map(champ => (
                <option key={champ.uuid} value={champ.uuid}>
                  {champ.name}
                </option>
              ))}
            </SelectField>
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t(`${I18N_PREFIX}.season_label`)}</Text>
            <SelectField
              {...register('season_uuid')}
              isDisabled={!championshipUuid}
              onChange={event => handleSeasonChange(event.target.value)}
            >
              <option value=''>{t(`${I18N_PREFIX}.all_option`)}</option>
              {(seasonsData?.data ?? []).map(season => (
                <option key={season.uuid} value={season.uuid}>
                  {season.year}
                  {season.type === SeasonType.HYBRID ? ` · ${t(`${I18N_PREFIX}.season_type_hybrid`)}` : ''}
                  {season.type === SeasonType.KNOCKOUT ? ` · ${t(`${I18N_PREFIX}.season_type_knockout`)}` : ''}
                  {season.type === SeasonType.LEAGUE ? ` · ${t(`${I18N_PREFIX}.season_type_league`)}` : ''}
                </option>
              ))}
            </SelectField>
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t(`${I18N_PREFIX}.stage_label`)}</Text>
            <SelectField
              {...register('stage_uuid')}
              isDisabled={!seasonUuid}
              onChange={event => handleStageChange(event.target.value)}
            >
              <option value=''>{t(`${I18N_PREFIX}.all_option`)}</option>
              {(stagesData?.data ?? []).map(stage => (
                <option key={stage.uuid} value={stage.uuid}>
                  {stage.group_name ? `${stage.name} · ${stage.group_name}` : stage.name}
                </option>
              ))}
            </SelectField>
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t(`${I18N_PREFIX}.round_label`)}</Text>
            <SelectField {...register('round')} isDisabled={!stageUuid}>
              <option value=''>{t(`${I18N_PREFIX}.all_option`)}</option>
              {roundOptions.map(round => (
                <option key={round} value={String(round)}>
                  {round}
                </option>
              ))}
            </SelectField>
          </InputGroup>

          <ControlTeamSelectAutocomplete
            control={control}
            name='team_uuid'
            clubs={seasonClubsSeed}
            width='100%'
            label={t(`${I18N_PREFIX}.team_label`)}
            placeholder={t(`${I18N_PREFIX}.all_option`)}
            disabled={!seasonUuid}
            showFlag
            prefetchOnOpen={false}
            minChars={9999}
          />

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t(`${I18N_PREFIX}.status_label`)}</Text>
            <SelectField {...register('status')}>
              <option value=''>{t(`${I18N_PREFIX}.all_option`)}</option>
              <option value={MatchStatus.SCHEDULED}>{t(`${I18N_PREFIX}.status_scheduled`)}</option>
              <option value={MatchStatus.RUNNING}>{t(`${I18N_PREFIX}.status_running`)}</option>
              <option value={MatchStatus.FINISHED}>{t(`${I18N_PREFIX}.status_finished`)}</option>
              <option value={MatchStatus.POSTPONED}>{t(`${I18N_PREFIX}.status_postponed`)}</option>
              <option value={MatchStatus.CANCELED}>{t(`${I18N_PREFIX}.status_canceled`)}</option>
              <option value={MatchStatus.SUSPENDED}>{t(`${I18N_PREFIX}.status_suspended`)}</option>
              <option value={MatchStatus.DRAFT}>{t(`${I18N_PREFIX}.status_draft`)}</option>
            </SelectField>
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t(`${I18N_PREFIX}.start_date_label`)}</Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(startDateControl.field.value)}
              onChange={event => startDateControl.field.onChange(event.target.value)}
            />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t(`${I18N_PREFIX}.end_date_label`)}</Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(endDateControl.field.value)}
              onChange={event => endDateControl.field.onChange(event.target.value)}
              min={formatDateToDateInputValue(startDateControl.field.value)}
            />
          </InputGroup>
        </SimpleGrid>

        <Button
          alignSelf={{ lg: 'end' }}
          colorScheme='text'
          variant='outline'
          mt='10px'
          onClick={() => reset()}
        >
          {t(`${I18N_PREFIX}.clear_button`)}
        </Button>
      </Flex>
    </Box>
  );
};
